import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RedeemIcon from '@mui/icons-material/Redeem';
import ShareIcon from '@mui/icons-material/Share';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import EmailIcon from '@mui/icons-material/Email';
import { NavLink, useParams } from 'react-router-dom';
import { get, patch } from '../../utils/api';
import { usePortalProfile } from '../../contexts/PortalProfileContext';
import { usePortalPoshaka } from '../../contexts/PortalPoshakaContext';
import PortalPageHeader from '../../components/portal/PortalPageHeader';
import { CloseX, ignoreBackdropEscapeClose } from '../../components/portal/portalUi';
import { getPortalQuickLinks, KOTILINGA_SHARE } from '../../config/portalHomeContent';
import { poshakaBenefitImageUrls } from '../../utils/poshakaBenefitImages';

const namasteListIconUrl = `${String(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/assets/images/namaste1.jpg`;

/** Namaste mark beside thank-you lines — uses `public/assets/images/namaste1.jpg`. */
function NamasteHandsIcon({ sx, ...rest }) {
  return (
    <Box
      component="img"
      src={namasteListIconUrl}
      alt=""
      aria-hidden
      {...rest}
      sx={{
        flexShrink: 0,
        display: 'block',
        width: 26,
        height: 26,
        objectFit: 'contain',
        objectPosition: 'center',
        alignSelf: 'flex-start',
        mt: '2px',
        ...sx,
      }}
    />
  );
}

function formatMoney(value, currency = 'USD') {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(value || 0));
  } catch {
    return `$${Number(value || 0).toFixed(2)}`;
  }
}

function isPledgeComplete(p) {
  if (!p) return false;
  const status = String(p.status || '').toLowerCase().trim();
  const balance = Number(p.balance_total ?? 0);
  return status === 'pledge-completed' || balance <= 0;
}

function pledgeStatusKey(status) {
  return String(status || '').toLowerCase().trim();
}

function formatPledgeStatusLabel(o) {
  if (isPledgeComplete(o)) return 'Completed';
  const s = pledgeStatusKey(o?.status);
  if (s === 'pledge-pending') return 'Pending';
  return o?.status ? String(o.status) : '—';
}

function sortPledgesForDisplay(list) {
  const pending = list.filter((o) => !isPledgeComplete(o));
  const completed = list.filter((o) => isPledgeComplete(o));
  const byDateDesc = (a, b) => {
    const ta = new Date(a.date_created_gmt || a.date_created || 0).getTime();
    const tb = new Date(b.date_created_gmt || b.date_created || 0).getTime();
    return tb - ta;
  };
  pending.sort(byDateDesc);
  completed.sort(byDateDesc);
  return [...pending, ...completed];
}

function pledgeDisplayName(o) {
  const items = Array.isArray(o.line_items) ? o.line_items : [];
  if (items.length === 0) return 'Pledge';
  return items
    .map((li) => {
      const q = li.quantity && Number(li.quantity) > 1 ? ` ×${Number(li.quantity)}` : '';
      return `${li.name || 'Item'}${q}`;
    })
    .join('; ');
}

function getFamilyNudge(familyMembers, occasionsPayload) {
  const members = Array.isArray(familyMembers) ? familyMembers : [];
  const nonPrimary = members.filter(
    (m) => !m.is_primary && String(m.relation || '').toLowerCase() !== 'self'
  );
  if (nonPrimary.length === 0) {
    return {
      key: 'add_family',
      message:
        'Complete your profile with family members (spouse, children, parents) and their important occasions so we can send benefit reminders and keep temple records accurate.',
    };
  }
  const byMemberOcc = {};
  (occasionsPayload || []).forEach((m) => {
    byMemberOcc[m.id] = Array.isArray(m.occasions) ? m.occasions : [];
  });
  const missing = nonPrimary.filter((m) => {
    const occs = byMemberOcc[m.id] || [];
    return !occs.some((o) => {
      const t = String(o.occasion_type || '').toLowerCase();
      return t === 'birthday' || t === 'marriage_anniversary';
    });
  });
  if (missing.length > 0) {
    return {
      key: 'add_occasions',
      message: `Complete your profile with birthdays and anniversaries for ${missing.map((x) => x.name || 'family').join(', ')} so we can send benefit reminders when those dates approach.`,
    };
  }
  return null;
}

function ExternalButton({ href, children, ...rest }) {
  return (
    <Button
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      endIcon={<OpenInNewIcon sx={{ fontSize: '1rem !important' }} />}
      {...rest}
    >
      {children}
    </Button>
  );
}

/** Same-origin app path (e.g. `/donate`) — use router; absolute http(s) URLs use ExternalButton. */
function isInternalAppPath(href) {
  const h = String(href || '').trim();
  return h.startsWith('/') && !h.startsWith('//');
}

function PortalHrefButton({ href, children, ...rest }) {
  if (isInternalAppPath(href)) {
    return (
      <Button component={NavLink} to={href} {...rest}>
        {children}
      </Button>
    );
  }
  return <ExternalButton href={href} {...rest}>{children}</ExternalButton>;
}

function ordersInCalendarYear(batch, year) {
  return batch.filter((o) => {
    const d = new Date(o.date_created_gmt || o.date_created || 0);
    return !Number.isNaN(d.getTime()) && d.getFullYear() === year;
  });
}

/** Annadanam from completed order line-item names (current calendar year). */
function detectYtdProgramFlagsFromOrders(ytdOrders) {
  const flags = { annadanam: false };
  for (const o of ytdOrders) {
    const items = Array.isArray(o.line_items) ? o.line_items : [];
    for (const li of items) {
      const n = String(li.name || '').toLowerCase();
      if (n.includes('annadanam') || n.includes('annadaanam') || n.includes('anna daan')) flags.annadanam = true;
    }
  }
  return flags;
}

/** Load line items + product / variation labels for portal home “recent contributions” (matches donations page facets). */
async function enrichRecentOrdersForHome(orders, getFn) {
  if (!Array.isArray(orders) || orders.length === 0) return orders;
  const details = await Promise.all(orders.map((o) => getFn(`/portal/orders/${o.id}`).catch(() => ({}))));
  const withLines = orders.map((o, i) => {
    const items = Array.isArray(details[i]?.items) ? details[i].items : [];
    const line_items = items.map((it) => ({
      id: it.item_id ?? it.id,
      name: String(it.name || ''),
      product_id: it.product_id,
      variation_id: it.variation_id,
      quantity: it.quantity,
    }));
    return { ...o, line_items };
  });
  const productIds = [
    ...new Set(withLines.flatMap((o) => (o.line_items || []).map((li) => li.product_id).filter(Boolean))),
  ];
  let variationLabelById = new Map();
  if (productIds.length > 0) {
    try {
      const resVars = await getFn('/portal/facets/variations', { product_id: productIds.join(',') });
      const vars = Array.isArray(resVars?.variations) ? resVars.variations : [];
      variationLabelById = new Map(
        vars.map((v) => {
          const attrs = Array.isArray(v.attributes) ? v.attributes : [];
          const fromAttrs = attrs
            .map((a) => {
              const name = (a.name || a.attribute || '').toString();
              const val = (a.option || a.value || '').toString();
              return name && val ? `${name}: ${val}` : (val || name);
            })
            .filter(Boolean)
            .join(', ');
          const label = String(v.display_name || '').trim() || fromAttrs || String(v.sku || '').trim();
          return [v.id, label];
        })
      );
    } catch {
      /* ignore */
    }
  }
  const productNameById = new Map();
  await Promise.all(
    productIds.map(async (pid) => {
      try {
        const pr = await getFn('/portal/facets/products', { product_id: String(pid) });
        const row = Array.isArray(pr?.products) ? pr.products[0] : null;
        if (row?.name) productNameById.set(pid, row.name);
      } catch {
        /* ignore */
      }
    })
  );
  return withLines.map((o) => ({
    ...o,
    line_items: (o.line_items || []).map((li) => ({
      ...li,
      product_name: productNameById.get(li.product_id) || '',
      variation_label: li.variation_id ? variationLabelById.get(li.variation_id) || '' : '',
    })),
  }));
}

export default function PortalHomePage() {
  const { id } = useParams();
  const { profile } = usePortalProfile();
  const { poshaka } = usePortalPoshaka();
  const base = `/devotees/${id}`;
  const quickLinks = React.useMemo(() => getPortalQuickLinks(), []);

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [prefs, setPrefs] = React.useState({
    newsletterOptIn: false,
    newsletterEmail: '',
    volunteerRegistered: false,
    volunteerInterest: null,
    occasionEmailReminders: true,
  });
  const [kotilinga, setKotilinga] = React.useState({ isSponsor: false });
  const [ytdProgramFlags, setYtdProgramFlags] = React.useState({ annadanam: false });
  const [familyMembers, setFamilyMembers] = React.useState([]);
  const [occasionMembers, setOccasionMembers] = React.useState([]);
  const [summary, setSummary] = React.useState({
    currency: 'USD',
    ytdTotal: 0,
    lastDonationAmount: 0,
    lastDonationDate: '',
    activePledges: 0,
    pledgeBalance: 0,
    latestStatementYear: null,
  });
  const [recentOrders, setRecentOrders] = React.useState([]);
  const [pledgeRows, setPledgeRows] = React.useState([]);

  const [benefitsOpen, setBenefitsOpen] = React.useState(false);
  const { img1: IMG1, img2: IMG2 } = poshakaBenefitImageUrls();

  const [volunteerAreas, setVolunteerAreas] = React.useState('');
  const [volunteerAvailability, setVolunteerAvailability] = React.useState('');
  const [volunteerSaving, setVolunteerSaving] = React.useState(false);
  const [volunteerMsg, setVolunteerMsg] = React.useState({ type: '', text: '' });
  const [volunteerDialogOpen, setVolunteerDialogOpen] = React.useState(false);
  const [newsletterSaving, setNewsletterSaving] = React.useState(false);
  const [newsletterMsg, setNewsletterMsg] = React.useState({ type: '', text: '' });

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [
          pledgesRes,
          yearsRes,
          ordersRes,
          famRes,
          occRes,
          prefsRes,
          kotiRes,
        ] = await Promise.all([
          get('/portal/pledge-totals/by-customer', {}),
          get('/portal/orders/contribution-years'),
          get('/portal/orders', { status: 'completed', page: 1, per_page: 100 }),
          get('/portal/devotees/family-members'),
          get('/portal/devotees/family-occasions'),
          get('/portal/me/preferences'),
          get('/portal/me/kotilinga-sponsor'),
        ]);

        if (cancelled) return;

        const prefData = prefsRes?.data && typeof prefsRes.data === 'object' ? prefsRes.data : {};
        setPrefs({
          newsletterOptIn: !!prefData.newsletterOptIn,
          newsletterEmail: String(prefData.newsletterEmail || ''),
          volunteerRegistered: !!prefData.volunteerRegistered,
          volunteerInterest: prefData.volunteerInterest || null,
          occasionEmailReminders: prefData.occasionEmailReminders !== false,
        });
        if (prefData.volunteerInterest) {
          setVolunteerAreas(String(prefData.volunteerInterest.areas || ''));
          setVolunteerAvailability(String(prefData.volunteerInterest.availability || ''));
        }

        setKotilinga({ isSponsor: !!kotiRes?.isSponsor });

        const famList = Array.isArray(famRes?.data) ? famRes.data : [];
        setFamilyMembers(famList);
        const occList = Array.isArray(occRes?.data) ? occRes.data : [];
        setOccasionMembers(occList);

        const pledgeList = Array.isArray(pledgesRes?.data) ? pledgesRes.data : [];
        setPledgeRows(pledgeList);
        const activeRows = pledgeList.filter((p) => !isPledgeComplete(p));
        const activePledges = activeRows.length;
        const pledgeBalance = activeRows.reduce((sum, p) => sum + Number(p.balance_total ?? 0), 0);

        const years = Array.isArray(yearsRes?.years) ? yearsRes.years.filter((y) => Number.isInteger(y)) : [];
        const latestStatementYear = years.length ? [...years].sort((a, b) => b - a)[0] : null;

        const batch = Array.isArray(ordersRes?.data) ? ordersRes.data : [];
        const sorted = [...batch].sort((a, b) => {
          const ta = new Date(a.date_created_gmt || a.date_created || 0).getTime();
          const tb = new Date(b.date_created_gmt || b.date_created || 0).getTime();
          return tb - ta;
        });
        const topRecent = sorted.slice(0, 5);
        const recentEnriched = await enrichRecentOrdersForHome(topRecent, get);
        if (!cancelled) setRecentOrders(recentEnriched);

        const nowYear = new Date().getFullYear();
        const ytdOrders = ordersInCalendarYear(batch, nowYear);
        setYtdProgramFlags(detectYtdProgramFlagsFromOrders(ytdOrders));
        const ytdTotal = ytdOrders.reduce((sum, o) => sum + Number(o.total_num ?? 0), 0);
        const latest = sorted[0];
        const lastDonationAmount = Number(latest?.total_num ?? 0);
        const lastDonationDate = latest?.date_created_gmt || latest?.date_created || '';
        const currency = latest?.currency || 'USD';

        setSummary({
          currency,
          ytdTotal,
          lastDonationAmount,
          lastDonationDate,
          activePledges,
          pledgeBalance,
          latestStatementYear,
        });
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Could not load dashboard.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const firstName = String(profile.first_name || '').trim();
  const displayName = firstName || String(profile.email || 'Devotee').trim();
  const familyNudge = React.useMemo(
    () => getFamilyNudge(familyMembers, occasionMembers),
    [familyMembers, occasionMembers]
  );
  const pledgesPreview = React.useMemo(() => sortPledgesForDisplay(pledgeRows).slice(0, 5), [pledgeRows]);

  const calendarYear = new Date().getFullYear();

  const programThankRows = React.useMemo(() => {
    const rows = [];
    if (poshaka?.isSubscriber) {
      const y = poshaka.year || calendarYear;
      const ribbon = poshaka.ribbon ? ` — ${poshaka.ribbon}` : '';
      rows.push({
        key: 'poshaka',
        text: `Thank you for your Poshaka support this year — Poshaka Parivar ${y}${ribbon}.`,
      });
    }
    if (kotilinga?.isSponsor) {
      rows.push({
        key: 'kotilinga',
        text: 'Thank you for supporting the Kotilinga initiative through your sponsorship.',
      });
    }
    if (ytdProgramFlags.annadanam) {
      rows.push({ key: 'annadanam', text: 'Thank you for supporting Annadanam.' });
    }
    return rows;
  }, [poshaka, kotilinga, ytdProgramFlags, calendarYear]);

  const hasFeaturedProgramSupport = programThankRows.length > 0;

  const howCanSupportRows = React.useMemo(
    () => [
      {
        key: 'poshaka',
        title: 'Poshaka Parivar',
        blurb: 'Yearly sponsorship with temple recognition and published benefits by tier.',
        donateHref: quickLinks.poshakaStore,
        detailsHref: quickLinks.poshakaAbout,
      },
      {
        key: 'kotilinga',
        title: 'Kotilinga project',
        blurb: 'Sponsor ICCT’s rare Kotilinga initiative and share the opportunity with others.',
        donateHref: quickLinks.donate,
        detailsHref: quickLinks.kotilingaInfo,
        showShare: true,
      },
      {
        key: 'annadanam',
        title: 'Annadanam',
        blurb: 'Help provide sacred food offering and community meals.',
        donateHref: quickLinks.donate,
        detailsHref: quickLinks.annadanamInfo,
      },
    ],
    [quickLinks]
  );

  const supportDetailsButtonSx = {
    bgcolor: 'common.white',
    color: 'primary.dark',
    borderColor: 'divider',
    '&:hover': {
      bgcolor: alpha('#1a237e', 0.04),
      borderColor: 'primary.light',
    },
  };

  const onShareKotilinga = async () => {
    const url = quickLinks.kotilingaInfo;
    const payload = { title: KOTILINGA_SHARE.title, text: `${KOTILINGA_SHARE.text} ${url}`, url };
    try {
      if (navigator.share) {
        await navigator.share(payload);
        return;
      }
    } catch {
      /* user cancelled or share failed */
    }
    const subject = encodeURIComponent(KOTILINGA_SHARE.title);
    const body = encodeURIComponent(`${KOTILINGA_SHARE.text}\n\n${url}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const onNewsletterOptIn = async () => {
    setNewsletterMsg({ type: '', text: '' });
    setNewsletterSaving(true);
    try {
      const out = await patch('/portal/me/newsletter/subscribe', {});
      const d = out?.data;
      const info = out?.newsletter || {};
      if (d && typeof d === 'object') {
        setPrefs((p) => ({
          ...p,
          newsletterOptIn: !!d.newsletterOptIn,
          newsletterEmail: String(d.newsletterEmail || p.newsletterEmail || profile.email || ''),
        }));
      }
      const msg =
        info.status === 'already_subscribed'
          ? `This email is already subscribed: ${info.email || d?.newsletterEmail || profile.email || ''}`
          : `Subscribed successfully: ${info.email || d?.newsletterEmail || profile.email || ''}`;
      setNewsletterMsg({ type: 'success', text: msg });
    } catch (e) {
      setNewsletterMsg({ type: 'error', text: e?.message || 'Could not save newsletter preference.' });
    } finally {
      setNewsletterSaving(false);
    }
  };

  const quickLinkButtons = React.useMemo(
    () => [
      { key: 'donate', label: 'Donate (Online donation)', href: quickLinks.donate },
      { key: 'pooja-booking', label: 'Pooja booking', href: quickLinks.poojaBooking },
      { key: 'pooja-materials', label: 'Pooja materials', href: quickLinks.poojaMaterials },
      { key: 'calendar', label: 'Calendar', href: quickLinks.calendar },
      { key: 'facility-rentals', label: 'Facility rentals', href: quickLinks.facilityRentals },
    ],
    [quickLinks]
  );

  const onVolunteerSubmit = async (e) => {
    e.preventDefault();
    setVolunteerMsg({ type: '', text: '' });
    const areas = volunteerAreas.trim();
    const availability = volunteerAvailability.trim();
    if (!areas && !availability) {
      setVolunteerMsg({ type: 'error', text: 'Please describe your interests or availability.' });
      return;
    }
    setVolunteerSaving(true);
    try {
      const out = await patch('/portal/me/preferences', { volunteerInterest: { areas, availability } });
      const d = out?.data;
      if (d && typeof d === 'object') {
        setPrefs((p) => ({
          ...p,
          volunteerRegistered: !!d.volunteerRegistered,
          volunteerInterest: d.volunteerInterest || null,
        }));
      }
      setVolunteerMsg({
        type: 'success',
        text: 'Thank you! Our team will follow up about volunteering opportunities.',
      });
      setVolunteerDialogOpen(false);
    } catch (err) {
      setVolunteerMsg({ type: 'error', text: err?.message || 'Could not submit volunteer interest.' });
    } finally {
      setVolunteerSaving(false);
    }
  };

  const cardSx = {
    height: '100%',
    borderRadius: 2,
    borderColor: 'divider',
    bgcolor: 'background.paper',
  };

  const namasteShellSx = {
    mb: 2.5,
    p: { xs: 2, sm: 3 },
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
    background:
      'linear-gradient(135deg, rgba(26,35,126,0.06) 0%, rgba(255,255,255,0.95) 45%, rgba(232,197,71,0.12) 100%)',
  };

  const sectionTitleSx = { fontWeight: 800, letterSpacing: 0.2, mb: 1 };

  return (
    <>
      <PortalPageHeader title="Home" />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {!loading && !error ? (
        <>
          <Paper elevation={0} sx={namasteShellSx}>
            <Grid container spacing={2} alignItems="flex-start" justifyContent="space-between">
              <Grid item xs={12} md={8} sx={{ minWidth: 0, pr: { md: 1 } }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.dark' }}>
                  Namaste, {displayName}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 720 }}>
                  {hasFeaturedProgramSupport
                    ? 'Thank you for your generous support of the temple; your support for the programs below means a great deal to ICCT.'
                    : 'Thank you for being a great supporter of the temple.'}
                </Typography>
                {hasFeaturedProgramSupport ? (
                  <Stack spacing={1} sx={{ mt: 1.5 }}>
                    {programThankRows.map((row) => (
                      <Stack key={row.key} direction="row" alignItems="flex-start" spacing={1}>
                        <NamasteHandsIcon sx={{ width: 28, height: 28, mt: 0.15 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 auto', minWidth: 0 }}>
                          {row.text}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'primary.dark' }}>
                      How can you support?
                    </Typography>
                    <Stack spacing={1.5}>
                      {howCanSupportRows.map((row) => (
                        <Box key={row.key}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                              {row.title}
                            </Box>
                            {' — '}
                            {row.blurb}
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                            <PortalHrefButton href={row.donateHref} size="small" variant="contained" color="primary">
                              Donate
                            </PortalHrefButton>
                            <ExternalButton
                              href={row.detailsHref}
                              size="small"
                              variant="outlined"
                              color="inherit"
                              sx={supportDetailsButtonSx}
                            >
                              More details
                            </ExternalButton>
                            {row.showShare ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                startIcon={<ShareIcon />}
                                onClick={onShareKotilinga}
                                sx={supportDetailsButtonSx}
                              >
                                Share with friends
                              </Button>
                            ) : null}
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                md="auto"
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                  width: { xs: '100%', md: 'auto' },
                  flexShrink: 0,
                  alignSelf: 'flex-start',
                }}
              >
                <Stack
                  direction="column"
                  spacing={1}
                  sx={{
                    alignItems: 'stretch',
                    width: { xs: '100%', md: 200 },
                    alignSelf: { xs: 'stretch', md: 'flex-end' },
                  }}
                >
                  <Paper
                    component={NavLink}
                    to={`${base}/donations`}
                    variant="outlined"
                    aria-label="View donations — donations this year"
                    sx={(theme) => ({
                      width: '100%',
                      minHeight: 96,
                      boxSizing: 'border-box',
                      px: 1,
                      py: 1,
                      borderRadius: 1.5,
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      textAlign: 'right',
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      borderColor: alpha(theme.palette.primary.dark, 0.45),
                      borderWidth: 1,
                      transition: theme.transitions.create(['background-color', 'box-shadow'], { duration: 150 }),
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                        boxShadow: theme.shadows[2],
                      },
                    })}
                  >
                    <Typography variant="body2" color="primary.dark" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.25 }}>
                      Donations this year
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 800, color: 'primary.dark', mt: 0.25, lineHeight: 1.25, whiteSpace: 'nowrap' }}
                    >
                      {formatMoney(summary.ytdTotal, summary.currency)}
                    </Typography>
                  </Paper>
                  <Paper
                    component={NavLink}
                    to={`${base}/pledges`}
                    variant="outlined"
                    aria-label="View pledges — active pledges"
                    sx={(theme) => ({
                      width: '100%',
                      minHeight: 96,
                      boxSizing: 'border-box',
                      px: 1,
                      py: 1,
                      borderRadius: 1.5,
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      textAlign: 'right',
                      bgcolor: alpha(theme.palette.secondary.main, 0.14),
                      borderColor: alpha(theme.palette.secondary.dark, 0.45),
                      borderWidth: 1,
                      transition: theme.transitions.create(['background-color', 'box-shadow'], { duration: 150 }),
                      '&:hover': {
                        bgcolor: alpha(theme.palette.secondary.main, 0.22),
                        boxShadow: theme.shadows[2],
                      },
                    })}
                  >
                    <Typography variant="body2" color="secondary.dark" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.25 }}>
                      Active pledges
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 800, color: 'secondary.dark', mt: 0.25, lineHeight: 1.25, whiteSpace: 'nowrap' }}
                    >
                      {summary.activePledges}
                    </Typography>
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600, display: 'block', mt: 0.25, lineHeight: 1.25, whiteSpace: 'nowrap' }}
                    >
                      ({formatMoney(summary.pledgeBalance, summary.currency)} balance)
                    </Typography>
                  </Paper>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {familyNudge ? (
            <Alert
              severity="info"
              sx={{ mb: 2.5 }}
              action={(
                <Button component={NavLink} to={`${base}/profile`} color="inherit" size="small">
                  Update profile
                </Button>
              )}
            >
              {familyNudge.message}
            </Alert>
          ) : null}

          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Stack spacing={2.5}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                    gap: 2,
                    width: '100%',
                    alignItems: 'stretch',
                  }}
                >
                  <Card variant="outlined" sx={{ ...cardSx, height: '100%', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Typography variant="h6" sx={sectionTitleSx}>Recent contributions</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          Thank you for your support — here are your latest completed donations.
                        </Typography>
                        {recentOrders.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">No completed donations yet.</Typography>
                        ) : (
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                  <TableCell sx={{ fontWeight: 700, minWidth: 0, maxWidth: { xs: 140, sm: 180 } }}>Product</TableCell>
                                  <TableCell sx={{ fontWeight: 700, minWidth: 0, maxWidth: { xs: 120, sm: 160 } }}>Variation</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {recentOrders.map((o) => (
                                  <TableRow key={o.id}>
                                    <TableCell>
                                      {o.date_created_gmt || o.date_created
                                        ? new Date(o.date_created_gmt || o.date_created).toLocaleDateString()
                                        : '—'}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 0, verticalAlign: 'top' }}>
                                      {(Array.isArray(o.line_items) && o.line_items.length > 0) ? (
                                        <Stack spacing={0.5}>
                                          {o.line_items.map((li, liIdx) => {
                                            const productLabel = String(li.product_name || '').trim() || li.name || '—';
                                            return (
                                              <Typography
                                                key={`${o.id}-p-${li.id ?? liIdx}`}
                                                variant="body2"
                                                sx={{ lineHeight: 1.35, overflowWrap: 'break-word', wordBreak: 'break-word' }}
                                              >
                                                {productLabel}
                                              </Typography>
                                            );
                                          })}
                                        </Stack>
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">—</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 0, verticalAlign: 'top' }}>
                                      {(Array.isArray(o.line_items) && o.line_items.length > 0) ? (
                                        <Stack spacing={0.5}>
                                          {o.line_items.map((li, liIdx) => {
                                            const v = String(li.variation_label || '').trim();
                                            return (
                                              <Typography
                                                key={`${o.id}-v-${li.id ?? liIdx}`}
                                                variant="body2"
                                                color={v ? 'text.primary' : 'text.secondary'}
                                                sx={{ lineHeight: 1.35, overflowWrap: 'break-word', wordBreak: 'break-word' }}
                                              >
                                                {v || '—'}
                                              </Typography>
                                            );
                                          })}
                                        </Stack>
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">—</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">{formatMoney(o.total_num, o.currency || summary.currency)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                        <Button component={NavLink} to={`${base}/donations`} sx={{ mt: 1.5 }} size="small">
                          View all donations
                        </Button>
                    </CardContent>
                  </Card>
                  <Card variant="outlined" sx={{ ...cardSx, height: '100%', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Typography variant="h6" sx={sectionTitleSx}>Recent pledges</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          Pending pledges appear first; open the pledges page for pay links and details.
                        </Typography>
                        {pledgesPreview.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">No pledges on file.</Typography>
                        ) : (
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 700 }}>Pledge</TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700 }}>Balance</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {pledgesPreview.map((p) => (
                                  <TableRow key={p.id ?? pledgeDisplayName(p)}>
                                    <TableCell sx={{ maxWidth: 220 }}>
                                      <Typography variant="body2" noWrap title={pledgeDisplayName(p)}>
                                        {pledgeDisplayName(p)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>{formatPledgeStatusLabel(p)}</TableCell>
                                    <TableCell align="right">{formatMoney(p.balance_total, p.currency || summary.currency)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                        <Button component={NavLink} to={`${base}/pledges`} sx={{ mt: 1.5 }} size="small">
                          View all pledges
                        </Button>
                    </CardContent>
                  </Card>
                </Box>

                <Card variant="outlined" sx={{ ...cardSx, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Typography variant="h6" sx={sectionTitleSx}>Quick links</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Temple updates and popular ICCT online services. Site links open in a new tab; online donate opens in this app when available.
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                        gap: 1,
                      }}
                    >
                      {quickLinkButtons.map((item) => (
                        <PortalHrefButton key={item.key} href={item.href} fullWidth variant="outlined" size="small">
                          {item.label}
                        </PortalHrefButton>
                      ))}
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        startIcon={<EmailIcon />}
                        onClick={onNewsletterOptIn}
                        disabled={newsletterSaving}
                      >
                        {newsletterSaving ? 'Subscribing…' : 'Newsletter subscription'}
                      </Button>
                    </Box>
                    <Box sx={{ mt: 1 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        startIcon={<VolunteerActivismIcon />}
                        onClick={() => {
                          setVolunteerMsg({ type: '', text: '' });
                          setVolunteerDialogOpen(true);
                        }}
                      >
                        Volunteering
                      </Button>
                    </Box>
                    <Box sx={{ mt: 1.5 }}>
                      {newsletterMsg.text ? (
                        <Alert severity={newsletterMsg.type === 'error' ? 'error' : 'success'} sx={{ mb: 1 }}>
                          {newsletterMsg.text}
                        </Alert>
                      ) : null}
                      {prefs.newsletterOptIn ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Subscribed email: <strong>{prefs.newsletterEmail || profile.email || '—'}</strong>
                        </Typography>
                      ) : null}
                    </Box>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>
          </Grid>

          <Dialog
            open={benefitsOpen}
            onClose={(e, reason) => {
              if (ignoreBackdropEscapeClose(e, reason)) return;
              setBenefitsOpen(false);
            }}
            disableEscapeKeyDown
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
              <Typography variant="h6" component="span">Poshaka benefits</Typography>
              <CloseX onClick={() => setBenefitsOpen(false)} />
            </DialogTitle>
            <DialogContent dividers>
              {IMG1 ? (
                <Box
                  component="img"
                  src={IMG1}
                  alt="Poshaka benefits"
                  sx={{ width: '100%', height: 'auto', display: 'block', mb: IMG2 ? 2 : 0, borderRadius: 1 }}
                />
              ) : null}
              {IMG2 ? (
                <Box
                  component="img"
                  src={IMG2}
                  alt="Poshaka benefits"
                  sx={{ width: '100%', height: 'auto', display: 'block', borderRadius: 1 }}
                />
              ) : null}
            </DialogContent>
          </Dialog>

          <Dialog
            open={volunteerDialogOpen}
            onClose={(e, reason) => {
              if (ignoreBackdropEscapeClose(e, reason)) return;
              setVolunteerDialogOpen(false);
            }}
            disableEscapeKeyDown
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
              <Typography variant="h6" component="span">Volunteering</Typography>
              <CloseX onClick={() => setVolunteerDialogOpen(false)} />
            </DialogTitle>
            <DialogContent dividers>
              <Box component="form" onSubmit={onVolunteerSubmit}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Interested in helping at the temple? Tell us your areas of interest and when you are typically
                  available.
                </Typography>
                <TextField
                  label="Areas of interest (events, kitchen, classes, IT, etc.)"
                  value={volunteerAreas}
                  onChange={(e) => setVolunteerAreas(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  label="Days / times you can help"
                  value={volunteerAvailability}
                  onChange={(e) => setVolunteerAvailability(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  sx={{ mb: 1.5 }}
                />
                {volunteerMsg.text ? (
                  <Alert severity={volunteerMsg.type === 'error' ? 'error' : 'success'} sx={{ mb: 1 }}>
                    {volunteerMsg.text}
                  </Alert>
                ) : null}
                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() => setVolunteerDialogOpen(false)}
                    disabled={volunteerSaving}
                  >
                    Close
                  </Button>
                  <Button type="submit" variant="contained" disabled={volunteerSaving}>
                    {volunteerSaving ? 'Submitting…' : 'Submit interest'}
                  </Button>
                </Stack>
              </Box>
            </DialogContent>
          </Dialog>
        </>
      ) : null}
    </>
  );
}
